// Editorial Warm home page (`/`).
//
// Pixel-targeted port of tmp/handoff/design/Nala Home.html →
// `direction-editorial.jsx`, restructured for nv-web's stack:
//   • Layout wrapper + booking modal + floating SMS live in
//     EditorialLayout, so this file just renders sections.
//   • Tailwind handles structural/responsive layout; inline styles cover
//     the bespoke editorial bits (hero gradient + feathering, FAQ
//     accordion animation, navy testimonial card, etc).
//   • "Book a visit" buttons fire the `nala-open-booking` event;
//     "Text us" fires `nala-open-sms` — both consumed by EditorialLayout.

import { useState } from 'react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import SEOHead, {
  NALA_BUSINESS_SCHEMA,
  WEBSITE_SCHEMA,
} from '../components/SEOHead'
import { NalaIcon, type NalaIconName } from '../components/nala/NalaIcon'
import { PhotoPlaceholder, AvatarPlaceholder } from '../components/nala/PhotoPlaceholder'
import AppPreview from '../components/nala/AppPreview'
import { nala } from '../components/nala/palette'

interface Service {
  icon: NalaIconName
  name: string
  body: string
}

const SERVICES: Service[] = [
  { icon: 'Stethoscope', name: 'Wellness exams',     body: 'Annual head-to-tail checks at home.' },
  { icon: 'Syringe',     name: 'Vaccinations',       body: 'On-schedule shots, gently delivered.' },
  { icon: 'Heart',       name: 'Sick visits',        body: "Same-week care when something's off." },
  { icon: 'HeartPulse',  name: 'Senior & chronic care', body: 'Ongoing support for aging pets and long-term conditions.' },
  { icon: 'Chip',        name: 'Microchipping',      body: 'Quick, lifetime ID, done in your kitchen.' },
  { icon: 'Paw',         name: 'Diagnostics',        body: 'Bloodwork and tests, samples taken at home.' },
  { icon: 'Plane',       name: 'Travel certificate', body: 'Health certificates for domestic and international travel.' },
  { icon: 'Leaf',        name: 'End-of-life',        body: "Peaceful, in-home hospice when it's time." },
]

const STEPS = [
  { n: '01', t: 'Book online',          b: 'Pick a window in seconds. We confirm by text.' },
  { n: '02', t: 'We come to you',       b: 'A licensed vet + tech arrive in our quiet van.' },
  { n: '03', t: 'Care, on the couch',   b: 'A full exam where your pet feels safest.' },
  { n: '04', t: 'Everything in the app',b: 'Visit notes, after care, and invoices, all in one place.' },
]

// Toggle to show/hide the "Meet the vets" section on the home page.
const SHOW_VETS = false

const VETS = [
  { n: 'Dr. Avery Okafor',  r: 'DVM',                 tone: 'sage'  as const, q: '"I started Nala because the kindest exam is the one where your pet doesn\'t notice it\'s happening."' },
  { n: 'Dr. Sarah Johnson', r: 'Senior vet · DVM',    tone: 'cream' as const, q: '"Cats deserve calm. We bring it with us."' },
  { n: 'Sasha Romero, RVT', r: 'Lead technician',     tone: 'blush' as const, q: '"Treats first. Always."' },
]

const FAQS = [
  { q: 'What areas do you serve?',                     a: "We serve a wide local area — text us your address and we'll confirm right away. Most of the time, the answer is yes." },
  { q: 'How is the price compared to a clinic?',       a: 'Visits run a bit higher than walk-in clinics because we bring the clinic to you. Most wellness visits land between $150–$220, including the trip fee.' },
  { q: 'Is in-home care safer for anxious pets?',      a: "For most pets, yes. Cortisol drops sharply when they're in a familiar space — exams are calmer and we can get a more accurate read." },
  { q: 'What if my pet needs surgery or X-rays?',      a: 'We partner with two trusted clinics for anything that needs a procedure room. We handle the referral and follow up afterward.' },
  { q: 'Do you take pet insurance?',                   a: "Yes — we provide itemized invoices that work with all major insurers. We don't bill them directly." },
]

const SECONDARY_TESTIMONIALS = [
  { q: 'No more dragging the carrier out. Honestly life-changing for our two senior dogs.', n: 'Priya & the pugs', s: 'Bi-annual visit' },
  { q: 'Booked Tuesday, treated Friday. Vaccinations done while she napped.',                n: 'Marcus & Penny',   s: 'Vaccinations' },
]

const openBooking = () =>
  window.dispatchEvent(new CustomEvent('nala-open-booking'))
const openSms = () =>
  window.dispatchEvent(new CustomEvent('nala-open-sms'))

export default function EditorialHomePage() {
  const [openFaq, setOpenFaq] = useState<number>(0)

  return (
    <>
      <SEOHead
        title="Nala — The vet your pet loves, because they never leave home."
        description="Nala is a house-call practice for dogs and cats in Miami-Dade. Same-week appointments, modern medicine, and the calm of your own living room."
        canonical="https://nala.vet"
        jsonLd={[NALA_BUSINESS_SCHEMA, WEBSITE_SCHEMA]}
      />

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ background: nala.bg }}
      >
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Copy column */}
            <div className="relative z-10 min-w-0">
              <h1
                className="text-[44px] sm:text-[64px] lg:text-[84px] max-w-full"
                style={{
                  fontFamily: nala.display,
                  fontWeight: 400,
                  lineHeight: 0.98,
                  letterSpacing: '-0.025em',
                }}
              >
                The vet your pet loves,{' '}
                <span style={{ color: nala.clay }}>
                  because they never leave home.
                </span>
              </h1>
              <p
                className="mt-5 text-lg sm:text-xl lg:text-[22px] max-w-xl"
                style={{
                  fontFamily: nala.display,
                  fontWeight: 400,
                  color: nala.mute,
                  lineHeight: 1.45,
                  letterSpacing: '-0.01em',
                }}
              >
                Nala is a house-call practice for dogs and cats. Same-week
                appointments, modern medicine, and the calm of your own living
                room.
              </p>
              <div className="mt-10 sm:mt-14">
                <div className="flex flex-wrap gap-3 items-stretch">
                  <button
                    type="button"
                    onClick={openBooking}
                    className="inline-flex items-center justify-center gap-2.5 rounded-full font-semibold transition-all hover:-translate-y-0.5"
                    style={{
                      background: nala.accent,
                      color: nala.accentInk,
                      width: 200,
                      padding: '14px 0',
                      fontSize: 15,
                      fontFamily: nala.sans,
                    }}
                  >
                    <NalaIcon.Calendar size={16} color={nala.accentInk} />
                    Book a visit
                  </button>
                  <button
                    type="button"
                    onClick={openSms}
                    className="inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors"
                    style={{
                      border: `1px solid ${nala.line}`,
                      color: nala.ink,
                      width: 200,
                      padding: '14px 0',
                      fontSize: 14,
                      fontFamily: nala.sans,
                    }}
                  >
                    <NalaIcon.Phone size={14} color={nala.ink} />
                    Text us
                  </button>
                </div>
                <div
                  className="hidden sm:inline-flex items-center gap-1.5 mt-3 text-xs whitespace-nowrap"
                  style={{ color: nala.mute, marginLeft: 212 }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{
                      background: nala.ok,
                      boxShadow: '0 0 0 2px rgba(58,138,85,0.2)',
                    }}
                  />
                  Current response time: under 2 min
                </div>
              </div>
            </div>

            {/* Hero animation — proportional on desktop, hidden on mobile to
                keep the fold focused on copy + CTAs */}
            <div className="hidden lg:block relative">
              <div
                className="overflow-hidden rounded-2xl"
                style={{
                  background: nala.soft,
                  height: 520,
                  boxShadow:
                    '0 24px 60px rgba(50,40,30,0.18), 0 4px 14px rgba(50,40,30,0.08)',
                }}
              >
                <DotLottieReact
                  src="/animations/nala_animation.lottie"
                  loop
                  autoplay
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section
        id="how"
        className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-20 lg:pb-24"
      >
        <div className="mb-9 max-w-3xl">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl"
            style={{
              fontFamily: nala.display,
              fontWeight: 400,
              letterSpacing: '-0.02em',
            }}
          >
            A calmer kind of vet visit.
          </h2>
          <p
            className="mt-3 text-lg sm:text-2xl"
            style={{
              fontFamily: nala.display,
              color: nala.mute,
              lineHeight: 1.4,
              letterSpacing: '-0.01em',
            }}
          >
            From booking to follow-up, the whole experience is built around your
            pet&apos;s comfort, and your schedule.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s) => (
            <Card key={s.n} className="p-6 min-h-[200px] flex flex-col justify-between">
              <div
                className="text-lg"
                style={{ fontFamily: nala.display, color: nala.clay }}
              >
                {s.n}
              </div>
              <div>
                <div
                  className="text-[22px] font-medium mb-1.5"
                  style={{ fontFamily: nala.display }}
                >
                  {s.t}
                </div>
                <div className="text-sm" style={{ color: nala.mute }}>
                  {s.b}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <AppPreview />
      </section>

      {/* ── SERVICES ─────────────────────────────────────────────────────── */}
      <section
        id="services"
        className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-20 lg:pb-24"
      >
        <h2
          className="text-3xl sm:text-4xl lg:text-[44px] mb-7"
          style={{
            fontFamily: nala.display,
            fontWeight: 400,
            letterSpacing: '-0.02em',
          }}
        >
          Everything a clinic does, at home.
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          {SERVICES.map((s) => {
            const Icon = NalaIcon[s.icon]
            return (
              <Card key={s.name} className="p-5 min-h-[160px] flex flex-col gap-2.5">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: nala.soft,
                    color: nala.accent,
                  }}
                >
                  <Icon size={18} color={nala.accent} />
                </div>
                <div
                  className="text-xl font-medium"
                  style={{ fontFamily: nala.display }}
                >
                  {s.name}
                </div>
                <div className="text-[13.5px]" style={{ color: nala.mute }}>
                  {s.body}
                </div>
              </Card>
            )
          })}
        </div>
      </section>

      {/* ── SERVICE AREA ────────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-20 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <h2
              className="text-3xl sm:text-4xl lg:text-[44px] mb-3.5"
              style={{
                fontFamily: nala.display,
                fontWeight: 400,
                letterSpacing: '-0.02em',
              }}
            >
              We come to you.
            </h2>
            <p className="text-[15px] leading-relaxed mb-5" style={{ color: nala.mute }}>
              Serving all of Miami-Dade county. Send us a quick text
              and we&apos;ll let you know what&apos;s possible at your address.
            </p>
            <button
              type="button"
              onClick={openSms}
              className="inline-flex items-center gap-2 rounded-full font-medium"
              style={{
                border: `1px solid ${nala.line}`,
                color: nala.ink,
                padding: '12px 18px',
                fontSize: 14,
              }}
            >
              <NalaIcon.Phone size={14} color={nala.ink} />
              Text us your address
            </button>
          </div>
          <div
            className="overflow-hidden w-full"
            style={{ aspectRatio: '4/3', background: '#ffffff' }}
          >
            <img
              src="/img/nala/florida-map.png"
              alt="Map of Florida highlighting Nala's Miami-Dade county service area"
              className="block w-full h-full object-contain"
              style={{ objectPosition: 'center center' }}
            />
          </div>
        </div>
      </section>

      {/* ── MEET THE VETS ────────────────────────────────────────────────── */}
      {SHOW_VETS && (
      <section
        id="vets"
        className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-20 lg:pb-24"
      >
        <h2
          className="text-3xl sm:text-4xl lg:text-[44px] mb-7"
          style={{
            fontFamily: nala.display,
            fontWeight: 400,
            letterSpacing: '-0.02em',
          }}
        >
          The same friendly face, every visit.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {VETS.map((v) => (
            <Card key={v.n} className="overflow-hidden p-0">
              <PhotoPlaceholder
                label={`portrait, ${v.n.split(' ')[1] ?? v.n}`}
                tone={v.tone}
                style={{ height: 220 }}
              />
              <div className="p-6">
                <div
                  className="text-[22px] font-medium"
                  style={{ fontFamily: nala.display }}
                >
                  {v.n}
                </div>
                <div
                  className="text-xs mt-0.5 font-medium"
                  style={{ color: nala.clay }}
                >
                  {v.r}
                </div>
                <div
                  className="mt-3 text-[15px] leading-relaxed"
                  style={{
                    fontFamily: nala.display,
                    color: nala.ink,
                  }}
                >
                  {v.q}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>
      )}

      {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-20 lg:pb-24">
        <h2
          className="text-3xl sm:text-4xl lg:text-[44px] mb-7"
          style={{
            fontFamily: nala.display,
            fontWeight: 400,
            letterSpacing: '-0.02em',
          }}
        >
          Pets are easier to read at home.
        </h2>
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Featured navy card spans 1 column on tablet, 1.2fr on desktop */}
          <div
            className="lg:col-span-1 p-7 rounded-[18px] flex flex-col justify-between"
            style={{
              background: nala.accent,
              color: nala.accentInk,
            }}
          >
            <div>
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <NalaIcon.Star key={i} size={14} color="#f3d49a" fill="#f3d49a" />
                ))}
              </div>
              <div
                className="text-[22px] sm:text-[26px]"
                style={{
                  fontFamily: nala.display,
                  fontWeight: 400,
                  lineHeight: 1.35,
                  letterSpacing: '-0.01em',
                }}
              >
                &ldquo;Our cat hides for two days after a clinic. With Nala, he
                sat on Dr. Sarah&apos;s lap and purred through the whole exam.&rdquo;
              </div>
            </div>
            <div className="mt-5 flex items-center gap-2.5">
              <AvatarPlaceholder size={36} tone="cream" label="J" />
              <div>
                <div className="font-semibold text-sm">Jordan &amp; Mochi</div>
                <div className="text-xs opacity-70">Wellness exam · Coral Gables</div>
              </div>
            </div>
          </div>

          {SECONDARY_TESTIMONIALS.map((t) => (
            <Card key={t.n} className="p-6">
              <div className="flex gap-1 mb-3.5">
                {Array.from({ length: 5 }).map((_, j) => (
                  <NalaIcon.Star
                    key={j}
                    size={12}
                    color={nala.clay}
                    fill={nala.clay}
                  />
                ))}
              </div>
              <div
                className="text-[19px]"
                style={{
                  fontFamily: nala.display,
                  fontWeight: 400,
                  lineHeight: 1.4,
                }}
              >
                &ldquo;{t.q}&rdquo;
              </div>
              <div className="mt-4 flex items-center gap-2.5">
                <AvatarPlaceholder size={32} tone="warm" label={t.n[0]} />
                <div>
                  <div className="font-semibold text-[13px]">{t.n}</div>
                  <div className="text-xs" style={{ color: nala.mute }}>
                    {t.s}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section
        id="faq"
        className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-20 lg:pb-24"
      >
        <div className="grid lg:grid-cols-[1fr_1.6fr] gap-8 lg:gap-14 items-start">
          <div>
            <h2
              className="text-3xl sm:text-4xl lg:text-[44px] mb-3.5"
              style={{
                fontFamily: nala.display,
                fontWeight: 400,
                letterSpacing: '-0.02em',
              }}
            >
              Questions, gently answered.
            </h2>
            <p className="text-[15px] leading-relaxed" style={{ color: nala.mute }}>
              Don&apos;t see yours? Text us at{' '}
              <span style={{ color: nala.ink, fontWeight: 600 }}>
                (213) 833-8007
              </span>{' '}
              — a real human replies within an hour during business days.
            </p>
          </div>
          <div>
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i
              return (
                <button
                  key={f.q}
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  className="block w-full text-left py-5"
                  style={{
                    borderBottom: `1px solid ${nala.line}`,
                    background: 'transparent',
                  }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <span
                      className="text-lg sm:text-xl font-medium"
                      style={{ fontFamily: nala.display }}
                    >
                      {f.q}
                    </span>
                    <span
                      className="flex items-center justify-center shrink-0"
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: isOpen ? nala.accent : nala.soft,
                        color: isOpen ? nala.accentInk : nala.ink,
                        transition: 'all .2s',
                      }}
                    >
                      <span
                        style={{
                          transform: isOpen ? 'rotate(45deg)' : 'rotate(0)',
                          transition: 'transform .2s',
                          display: 'inline-flex',
                        }}
                      >
                        <NalaIcon.Plus
                          size={12}
                          color={isOpen ? nala.accentInk : nala.ink}
                        />
                      </span>
                    </span>
                  </div>
                  <div
                    style={{
                      maxHeight: isOpen ? 200 : 0,
                      overflow: 'hidden',
                      transition: 'max-height .3s ease, margin-top .3s',
                      marginTop: isOpen ? 10 : 0,
                      color: nala.mute,
                      fontSize: 14.5,
                      lineHeight: 1.6,
                      paddingRight: 40,
                    }}
                  >
                    {f.a}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── CTA STRIP ────────────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-16">
        <div
          className="rounded-[18px] grid lg:grid-cols-[1.4fr_1fr] gap-6 items-center px-8 py-12 sm:py-14"
          style={{ background: nala.surface, border: `1px solid ${nala.line}` }}
        >
          <div>
            <h2
              className="text-3xl sm:text-4xl lg:text-[56px]"
              style={{
                fontFamily: nala.display,
                fontWeight: 400,
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
              }}
            >
              Your couch. Their checkup.
            </h2>
            <p
              className="mt-3.5 text-base max-w-md"
              style={{ color: nala.mute }}
            >
              First visits booked this week get a complimentary nose-to-tail
              wellness assessment.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <button
              type="button"
              onClick={openBooking}
              className="inline-flex items-center gap-2.5 rounded-full font-semibold"
              style={{
                background: nala.accent,
                color: nala.accentInk,
                padding: '14px 22px',
                fontSize: 15,
              }}
            >
              <NalaIcon.Calendar size={16} color={nala.accentInk} />
              Book a visit
            </button>
            <a
              href="tel:+12138338007"
              className="inline-flex items-center gap-2 rounded-full font-medium no-underline"
              style={{
                border: `1px solid ${nala.line}`,
                color: nala.ink,
                padding: '14px 18px',
                fontSize: 14,
              }}
            >
              <NalaIcon.Phone size={14} color={nala.ink} />
              (213) 833-8007
            </a>
          </div>
        </div>
      </section>
    </>
  )
}

// Editorial card surface — surface bg, soft border, lift on hover.
function Card({ className = '', children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={`transition-all duration-200 hover:-translate-y-0.5 ${className}`}
      style={{
        background: nala.surface,
        borderRadius: nala.radius,
        border: `1px solid ${nala.line}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 14px 40px rgba(50,40,30,0.08)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = ''
      }}
    >
      {children}
    </div>
  )
}
